import React from 'react';
import './UserAvatar.css';

interface UserAvatarProps {
  /** Avatar image URL */
  src?: string;
  /** Alt text for avatar image */
  alt?: string;
  /** Display name of the user */
  username?: string;
  /** User's current muse cash amount */
  museCash?: number;
  /** Emoji fallback if no image provided */
  emoji?: string;
  /** Avatar style variant */
  variant?: 'circular' | 'profile-card';
  /** Avatar size */
  size?: 'small' | 'medium' | 'large' | 'xl' | 'responsive';
  /** Whether avatar is clickable */
  clickable?: boolean;
  /** Click handler for avatar */
  onClick?: () => void;
  /** Logout handler for profile card variant */
  onLogout?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Whether to show special effects (glow, animation) */
  special?: boolean;
  /** Loading state */
  loading?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  src,
  alt = 'User avatar',
  username,
  museCash,
  emoji = '👤',
  variant = 'circular',
  size = 'medium',
  clickable = false,
  onClick,
  onLogout,
  className = '',
  style = {},
  special = false,
  loading = false
}) => {
  const baseClass = 'user-avatar';
  const classes = [
    baseClass,
    `${baseClass}--${size}`,
    `${baseClass}--${variant}`,
    clickable && `${baseClass}--clickable`,
    special && `${baseClass}--special`,
    loading && `${baseClass}--loading`,
    className
  ].filter(Boolean).join(' ');

  // Handle click events
  const handleClick = (e: React.MouseEvent) => {
    if (clickable && onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const handleLogoutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onLogout) {
      onLogout();
    }
  };

  if (variant === 'profile-card') {
    return (
      <div className={classes} style={style} onClick={handleClick}>
        {/* Circle avatar */}
        <div className={`${baseClass}__circle`}>
          {src ? (
            <img
              src={src}
              alt={alt}
              className={`${baseClass}__image`}
              loading="lazy"
            />
          ) : (
            <div className={`${baseClass}__emoji`}>
              {loading ? '⏳' : emoji}
            </div>
          )}
        </div>

        {/* User info panel */}
        <div className={`${baseClass}__info`}>
          {username && (
            <div className={`${baseClass}__username`}>
              {username}
            </div>
          )}
          {museCash !== undefined && (
            <div className={`${baseClass}__cash`}>
              💰 {museCash.toLocaleString()}
            </div>
          )}
          {onLogout && (
            <button
              className={`${baseClass}__logout`}
              onClick={handleLogoutClick}
              aria-label="Logout"
              type="button"
            >
              🚪
            </button>
          )}
        </div>
      </div>
    );
  }

  // Circular variant (simple)
  return (
    <div className={classes} style={style} onClick={handleClick}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className={`${baseClass}__image`}
          loading="lazy"
        />
      ) : (
        <div className={`${baseClass}__emoji`}>
          {loading ? '⏳' : emoji}
        </div>
      )}
    </div>
  );
};

export default UserAvatar; 